import React, { useEffect, useRef, useState } from "react";
import api from "../../../api/api";
import { COLUMNTITLES } from "../../../utils/Enum";
import Indices from "../Indices";
import Logger from "../../../utils/Logger";

interface IndicesType {
  year: number;
  indices: {
    indicesTitle: string;
    source: string;
    year: number;
    checked: boolean;
    status: string;
  }[];
}

const ArchiveEnvironmentIndices = () => {
  const [archiveEnvironmentIndicesArray, setArchiveEnvironmentalIndicesArray] =
    useState<IndicesType[]>([]);
  const [environmentalIndicesVisible, setEnvironmentalIndicesVisible] =
    useState<number[]>([]);
  const didArchiveEnvironmentGotCalled = useRef(false);

  const archiveEnvironmentIndices = async () => {
    try {
      Logger.info("Fetching archived environment indices", [
        "ArchiveEnvironmentIndices",
      ]);

      const archiveEnvironmentIndicesResults = await api.get(
        `/archived-indices/environment`,
      );
      const data = archiveEnvironmentIndicesResults.data.responseObject;

      if (Array.isArray(data)) {
        setArchiveEnvironmentalIndicesArray(data);
        sessionStorage.setItem(
          "archivedEnvironmentIndices",
          JSON.stringify(data),
        );
        Logger.info(
          "Successfully fetched and stored archived environment indices",
          ["ArchiveEnvironmentIndices"],
        );
      } else {
        Logger.error("API response is not an array", data, [
          "ArchiveEnvironmentIndices",
        ]);
      }
    } catch (error) {
      Logger.error("Error fetching archive environment indices", error, [
        "ArchiveEnvironmentIndices",
      ]);
    }
  };

  useEffect(() => {
    const storedArchivedEnvironmentIndices = sessionStorage.getItem(
      "archivedEnvironmentIndices",
    );

    if (storedArchivedEnvironmentIndices) {
      Logger.debug(
        "Loading archived environment indices from session storage",
        ["ArchiveEnvironmentIndices"],
      );

      const parsedData = JSON.parse(storedArchivedEnvironmentIndices);

      if (Array.isArray(parsedData)) {
        setArchiveEnvironmentalIndicesArray(parsedData);
      } else if (
        typeof parsedData === "object" &&
        parsedData !== null &&
        !Array.isArray(parsedData)
      ) {
        // Transform the object into an array
        const transformedData = Object.entries(
          parsedData as Record<string, unknown>,
        ).map(([year, indices]) => ({
          year: Number(year),
          indices: indices as IndicesType["indices"],
        }));
        setArchiveEnvironmentalIndicesArray(transformedData);
        Logger.info("Transformed stored object data to array format", [
          "ArchiveEnvironmentIndices",
        ]);
      } else if (!didArchiveEnvironmentGotCalled.current) {
        Logger.warn("No valid stored data found, fetching from API", [
          "ArchiveEnvironmentIndices",
        ]);
        archiveEnvironmentIndices();
        didArchiveEnvironmentGotCalled.current = true;
      } else {
        Logger.error(
          "Stored data is not an array or valid object",
          parsedData,
          ["ArchiveEnvironmentIndices"],
        );
      }
    }
  }, []);

  const handleArrowClick = (year: number) => {
    Logger.debug(`Toggle visibility for year ${year}`, [
      "ArchiveEnvironmentIndices",
    ]);

    setEnvironmentalIndicesVisible((prev) =>
      prev.includes(year)
        ? prev.filter((currentYear) => currentYear !== year)
        : [...prev, year],
    );
  };

  return (
    <>
      {Array.isArray(archiveEnvironmentIndicesArray) &&
        archiveEnvironmentIndicesArray.map((indices, index) => (
          <div key={index}>
            <div className="flex text-[24px] font-medium not-italic ml-[50px] m-[24px]">
              {indices.year}{" "}
              <button
                className="mx-[16px] p-[8px]"
                onClick={() => handleArrowClick(indices.year)}
                id="arrow-up-button"
              >
                <div
                  className={`${environmentalIndicesVisible.includes(indices.year) ? "icon-keyboard_arrow_up" : "icon-keyboard_arrow_down"} text-2xl text-blue-600`}
                />
              </button>
            </div>
            {environmentalIndicesVisible.includes(indices.year) && (
              <div>
                <Indices
                  categoryTab={COLUMNTITLES.ARCHIVED}
                  risksScoreIndices={indices.indices}
                />
              </div>
            )}
          </div>
        ))}
    </>
  );
};

export default ArchiveEnvironmentIndices;
